// =====================
// Heuristic Farming SDK-Free FE Integration
// =====================

import BigNumber from "bignumber.js";
import { Contract } from "ethers";
import NFTFarmingBaseExpABI from "../abis/FarmingBaseEXP.json";
import NFTCollectionABI from "../abis/NFTCollectionABI.json";
import NFTCoreABI from "../abis/NFTCoreABI.json";
import multiCall from "../../utils/multicall";

const DAY_TO_SECONDS = 24 * 60 * 60;
const BASE18 = BigNumber("1000000000000000000");
const RarityMapping = ["copper", "silver", "gold", "diamond", "crystal", "limited"];

const heuristicFamingConfig = {
  97: {
    "copper-vault": { id: "copper-vault", level: 1, name: "Copper", rarity: "copper", collectionPrice: 4000 },
    "silver-vault": { id: "silver-vault", level: 2, name: "Silver", rarity: "silver", collectionPrice: 10600 },
    "gold-vault": { id: "gold-vault", level: 3, name: "Gold", rarity: "gold", collectionPrice: 23600, disabled: true },
    "diamond-vault": {
      id: "diamond-vault",
      level: 4,
      name: "Diamond",
      rarity: "diamond",
      collectionPrice: 52000,
      disabled: true,
    },
    "crystal-vault": {
      id: "crystal-vault",
      level: 5,
      name: "Crystal",
      rarity: "crystal",
      collectionPrice: 150000,
      disabled: true,
    },
  },
  56: {
    "copper-vault": { id: "copper-vault", level: 1, name: "Copper", rarity: "copper", collectionPrice: 9000 },
    "silver-vault": { id: "silver-vault", level: 2, name: "Silver", rarity: "silver", collectionPrice: 22000 },
    "gold-vault": { id: "gold-vault", level: 3, name: "Gold", rarity: "gold", collectionPrice: 64000 },
    "diamond-vault": { id: "diamond-vault", level: 4, name: "Diamond", rarity: "diamond", collectionPrice: 150000 },
    "crystal-vault": { id: "crystal-vault", level: 5, name: "Crystal", rarity: "crystal", collectionPrice: 820000 },
  },
};

async function getHeuristicFarmingData(
  userAddress,
  provider,
  CHAIN_ID,
  FARMING_ADDRESS,
  COLLECTION_ADDRESS,
  CORE_ADDRESS
) {
  const results = new Map();

  const vaults = heuristicFamingConfig[CHAIN_ID];
  const NFTHeuristicContract = new Contract(FARMING_ADDRESS, NFTFarmingBaseExpABI, provider);
  const NFTCollectionContract = new Contract(COLLECTION_ADDRESS, NFTCollectionABI, provider);

  for (const vaultId in vaults) {
    if (vaults.hasOwnProperty(vaultId)) {
      const vault = vaults[vaultId];
      const [idList, poolInfos, eps] = await Promise.all([
        NFTHeuristicContract.getStakingNFTinALevel(userAddress, vault.level),
        NFTHeuristicContract.poolInfos(vault.level),
        NFTHeuristicContract.getEmissionPerSecond(vault.level),
      ]);

      const epsValue = eps?.hex ? BigNumber(eps.hex) : BigNumber(eps.toString());

      const dailyReward = epsValue.multipliedBy(DAY_TO_SECONDS).dividedBy(BASE18).toNumber();

      const numberKnightOfUser = idList.length;
      const totalNFTs = poolInfos.nftCount;
      const totalVaultValue = Number(poolInfos.totalValue);

      const idList1 = idList.map((id) => id.toString());

      const totalRewardOfUser = await NFTHeuristicContract.getTotalRewardsBalance(idList1);
      const totalRewardValue = totalRewardOfUser?.hex
        ? BigNumber(totalRewardOfUser.hex)
        : BigNumber(totalRewardOfUser.toString());

      const [nftInformation, expEarned, exp, balance] = await Promise.all([
        multiCall(
          NFTFarmingBaseExpABI,
          idList.map((id) => ({
            address: FARMING_ADDRESS,
            name: "nftInfos",
            params: [id],
          })),
          provider
        ),
        multiCall(
          NFTFarmingBaseExpABI,
          idList.map((id) => ({
            address: FARMING_ADDRESS,
            name: "getExpEarnedAndValueEarned",
            params: [id],
          })),
          provider
        ),
        multiCall(
          NFTCollectionABI,
          idList.map((id) => ({
            address: COLLECTION_ADDRESS,
            name: "getCollectionExperience",
            params: [id],
          })),
          provider
        ),
        multiCall(
          NFTFarmingBaseExpABI,
          idList.map((id) => ({
            address: FARMING_ADDRESS,
            name: "getTotalRewardsBalance",
            params: [[id]],
          })),
          provider
        ),
      ]);

      const nftInfos = nftInformation.map((data, index) => ({
        attainedExp: parseInt(exp[index][0]),
        depositedTime: parseInt(data.depositTime) * 1000,
        lastPolishTime: parseInt(data.lastPolishTime) * 1000,
        id: Number(idList[index]),
        exp: Number(expEarned[index][0]),
        earn: BigNumber(balance[index]).dividedBy(BASE18).toNumber(),
        value: Number(expEarned[index][1]),
      }));

      const collectionMetadataArray = await Promise.all(
        idList1.map(async (id, index) => {
          const collectionMetadata = await NFTCollectionContract.getCollectionMetadata(id);

          const itemIdList = collectionMetadata[0];
          const [itemsMetadata] = await Promise.all([
            multiCall(
              NFTCoreABI,
              itemIdList.map((tokenId) => ({
                address: CORE_ADDRESS,
                name: "getTokenMetadata",
                params: [tokenId],
              })),
              provider
            ),
          ]);

          const [armorMetadata, helmetMetadata, shieldMetadata, weaponMetadata] = itemsMetadata;
          const rarityStr = RarityMapping[parseInt(collectionMetadata[1]) - 1];
          const price = vaults[`${rarityStr}-vault`].collectionPrice;

          return {
            armorMetadata,
            helmetMetadata,
            shieldMetadata,
            weaponMetadata,
            collectionMetadata,
            apr: calculateKnightApr(dailyReward, nftInformation[index].value, totalVaultValue, price),
          };
        })
      );

      const farmingKnightDetailInfos = nftInfos.map((knight, i) => ({
        ...knight,
        apr: collectionMetadataArray[i].apr,
        rarity: collectionMetadataArray[i].collectionMetadata.rarity,
        armor: {
          tokenId: collectionMetadataArray[i].collectionMetadata.armorTokenId,
          rarity: parseInt(collectionMetadataArray[i].armorMetadata[0].tokenRarity),
          exp: parseInt(collectionMetadataArray[i].armorMetadata[0].experiencePoint),
        },
        helmet: {
          tokenId: collectionMetadataArray[i].collectionMetadata.helmetTokenId,
          rarity: parseInt(collectionMetadataArray[i].helmetMetadata[0].tokenRarity),
          exp: parseInt(collectionMetadataArray[i].helmetMetadata[0].experiencePoint),
        },
        shield: {
          tokenId: collectionMetadataArray[i].collectionMetadata.shieldTokenId,
          rarity: parseInt(collectionMetadataArray[i].shieldMetadata[0].tokenRarity),
          exp: parseInt(collectionMetadataArray[i].shieldMetadata[0].experiencePoint),
        },
        weapon: {
          tokenId: collectionMetadataArray[i].collectionMetadata.weaponTokenId,
          rarity: parseInt(collectionMetadataArray[i].weaponMetadata[0].tokenRarity),
          exp: parseInt(collectionMetadataArray[i].weaponMetadata[0].experiencePoint),
        },
      }));

      const nftFarming = {
        ...vault,
        aprAvg: calculateVaultApr(dailyReward, Number(totalNFTs), vault.collectionPrice),
        numberKnightOfUser,
        totalNFTs: Number(totalNFTs),
        totalRewardOfUser: totalRewardValue.dividedBy(BASE18).toNumber(),
        totalVaultValue,
        dailyReward,
        collection: farmingKnightDetailInfos,
      };

      results.set(vaultId, nftFarming);
    }
  }

  return results;
}

function calculateKnightApr(dailyReward, collectionVaultValue, totalVaultValue, collectionPrice) {
  if (totalVaultValue === 0 || collectionPrice === 0) return 0;
  return BigNumber(dailyReward)
    .multipliedBy(collectionVaultValue)
    .div(totalVaultValue)
    .multipliedBy(365)
    .div(collectionPrice)
    .multipliedBy(100)
    .toNumber();
}

function calculateVaultApr(dailyReward, totalNFTs, collectionPrice) {
  if (totalNFTs === 0 || collectionPrice === 0) return 0;
  return BigNumber(dailyReward).multipliedBy(365).div(totalNFTs).div(collectionPrice).multipliedBy(100).toNumber();
}

export default {
  getHeuristicFarmingData,
  calculateKnightApr,
  calculateVaultApr,
  heuristicFamingConfig,
  DAY_TO_SECONDS,
  BASE18,
  RarityMapping,
};
